/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import { useRef } from 'react';
import { UploadFile, message } from 'antd';
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';
import { AxiosProgressEvent } from 'axios';
import services from 'common/service';
import { UploadFileType } from 'common/constants';

const disConsole = true;

type Slice = {
  seq: number;
  start?: number;
  end?: number;
  isFileChunk: boolean;
  // 针对上传文件列表的情况，上传任务池——以分片为单位划分的任务集合，故需要每个数据携带fileMd5来维护数据信息
  file: UploadFile & File;
  batchUuid: string;
};

const formatRequestProgress = ({ event }: AxiosProgressEvent) => {
  let percent = 0;
  const progress = Number((event.loaded / event.total).toFixed(2));
  percent = progress === 1 ? 0.98 : progress;
  return percent;
};

type UploadTask = {
  slice: Slice;
  controller: AbortController;
  isFetching: boolean;
};

enum UploadStatus {
  WAITING = 'WAITING',
  PAUSE = 'PAUSE',
  UPLOADING = 'UPLOADING',
  FAIL = 'FAIL',
  SUCCESS = 'SUCCESS',
  DELETED = 'DELETED',
  ASSEMBLE = 'ASSEMBLE', // 服务端-组装分片
}
// （size1 ✖️ fetchProgress1 ➕ size2 ✖️ fetchProgress2 ➕ size3 ✖️ fetchProgress3  ）/ totalSize
type SliceUploadingStatus = Slice & {
  size: number;
  progress: number;
};

type CompletedCount = {
  [key: string]: {
    uploadStatus: SliceUploadingStatus[];
    name: string;
    fileMd5: string;
    shuyuanId: string; // shuyuan分片上传需要
    file: UploadFile & File;
  };
};

type SliceQueue = {
  [key: string]: Slice[];
};
type TaskStack = {
  [key: string]: UploadTask[];
};
type RetryMap = {
  [key: string]: {
    [key: number]: number;
  };
};

type BatchUploadInfo = {
  duration: number;
  totalSize: number;
};

type BatchUploadProps = {
  files: any[];
  batchUuid?: string;
  fileType: UploadFileType;
  onSuccess?: (file, batchUuid, batchUploadInfo: BatchUploadInfo) => void; // 文件集合全部上传成功
  onError?: (file, batchUuid) => void; // 文件集合上传失败
  onTotalProgress?: (batchUuid, fileMd5, progress, totalProgress) => void;
  onFileSuc?: (file, batchUuid, downloadUrl: string) => void; //单个文件上传成功
  onFileFail?: (file, batchUuid) => void; //单个文件上传失败
};

type Props = {
  CHUNK_SIZE?: number; // 50M分片
  CONCURRENCY_MAX?: number; // 网络并行请求数量限制
  RETRY_MAX?: number; // 请求错误重试次数限制
  openSentry?: boolean;
};
export const useBatchUpload = (props?: Props) => {
  const { CHUNK_SIZE = 1024 * 1024 * 8, CONCURRENCY_MAX = 1, RETRY_MAX = 3, openSentry = false } = props || {};
  // 分片上传相关字段
  // 支持上传多个文件，以batchUuid作为对应文件数据的索引，e.g status:{fileMd51: UploadStatus.UPLOADING}
  const startTime = useRef<Record<string, number>>({});
  const totalSize = useRef<Record<string, number>>({});
  const status = useRef<Record<string, string>>({});
  const completedCountRef = useRef<Record<string, CompletedCount>>({}); // 判断分片传输是否完成

  /**
   * utils function
   * @func initBatchUploadStatus
   * 初始化当前上传批次的状态记录
   * @param batchUuid 当前上传批次的唯一标识
   * @param filesSize 当前上传的所有文件size总和
   * @return null
   */
  const initBatchUploadStatus = (batchUuid, filesSize) => {
    !disConsole &&
      console.log('%c 执行[initBatchUploadStatus]', 'font-size:14px; background:pink; color:#bf2c9f;', {
        batchUuid,
        filesSize,
      });
    Object.assign(startTime.current, { [`${batchUuid}`]: Date.now() });
    Object.assign(totalSize.current, { [`${batchUuid}`]: filesSize });
    Object.assign(status.current, { [`${batchUuid}`]: {} });
    Object.assign(completedCountRef.current, { [`${batchUuid}`]: {} });
  };

  const sliceQueue = useRef<SliceQueue>({}); // 分片集合
  const taskStack = useRef<TaskStack>({}); // 网络并行请求进程集合
  const retryMap = useRef<RetryMap>({}); // 记录分片的重试次数
  const shuyuanSliceTags = useRef<Record<string, string>>({}); // 收集shuyuan分片标识，upload_chunk返回，供组装分片使用

  /**
   * utils function
   * @func initialFileChunkUpload
   * 触发分片上传逻辑的文件的初始化
   * @param batchUuid 当前上传批次的唯一标识
   * @param fileMd5 当前上传文件的唯一标识
   * @param totalSlices 当前上传文件的分片集合信息-计算进度使用
   * @param file 当前上传文件
   * @return null
   *
   * 步骤
   * 1.获取数源sdk id，以供分片
   * 2.初始化该文件的状态记录
   */
  const initialFileChunkUpload = async (batchUuid, fileMd5, totalSlices, file) => {
    !disConsole &&
      console.log(
        '%c 执行[initialFileChunkUpload]',
        'font-size:14px; background:red; color:#bf2c9f;',
        {
          batchUuid,
          file,
          totalSlices,
          fileMd5,
        },
        JSON.stringify(totalSlices),
      );
    const shuyuanId = await services.upload.getFileMd5(file.name);
    Object.assign(sliceQueue.current, { [`${fileMd5}`]: totalSlices }); // 分片集合
    Object.assign(taskStack.current, { [`${fileMd5}`]: [] }); // 网络并行请求进程集合
    Object.assign(retryMap.current, { [`${fileMd5}`]: {} });
    Object.assign(shuyuanSliceTags.current, { [`${fileMd5}`]: [] }); // 分片集合
    Object.assign(completedCountRef.current?.[`${batchUuid}`], {
      [`${fileMd5}`]: { name: file?.name, fileMd5, file, shuyuanId, uploadStatus: [...totalSlices] },
    });
  };

  /**
   * utils function
   * @func initialFileNormalUpload
   * 触发分片上传逻辑的文件的初始化
   * @param batchUuid 当前上传批次的唯一标识
   * @param fileMd5 当前上传文件的唯一标识
   * @param totalSlices 当前上传文件的分片集合信息-计算进度使用
   * @param file 当前上传文件
   * @return null
   *
   * 步骤
   * 1.获取数源sdk id，以供分片
   * 2.初始化该文件的状态记录
   */
  const initialFileNormalUpload = async (batchUuid, fileMd5, totalSlices, file) => {
    !disConsole &&
      console.log(
        '%c 执行[initialFileNormalUpload]',
        'font-size:14px; background:pink; color:#bf2c9f;',
        {
          batchUuid,
          file,
          totalSlices,
        },
        JSON.stringify(totalSlices),
      );
    Object.assign(sliceQueue.current, { [`${fileMd5}`]: totalSlices }); // 分片集合
    Object.assign(taskStack.current, { [`${fileMd5}`]: [] }); // 网络并行请求进程集合
    Object.assign(retryMap.current, { [`${fileMd5}`]: {} });
    Object.assign(completedCountRef.current?.[`${batchUuid}`], {
      [`${fileMd5}`]: { name: file?.name, fileMd5, file, uploadStatus: [...totalSlices] },
    });
  };

  /**
   * utils function
   * @func cancelRequests
   * 取消某个文件上传中的网络请求
   * @param fileMd5 当前上传文件的唯一标识
   * @return null
   *
   * 步骤
   * 1.取消进行中的网络请求
   */
  const cancelRequests = fileMd5 => {
    !disConsole &&
      console.log('%c 执行[cancelRequests]', 'font-size:14px; background:pink; color:#bf2c9f;', {
        fileMd5,
      });
    taskStack.current?.[`${fileMd5}`]?.forEach?.((item: UploadTask) => {
      item?.controller?.abort('ABANDONED_BY_USER');
      item.isFetching = false;
    });
  };

  /**
   * utils function
   * @func getConcurrencyUploadingCount
   * 计算上传中的网络请求
   * @return number
   *
   */
  const getConcurrencyUploadingCount = () => {
    const count = (Object.values(taskStack?.current) as Array<any>)?.reduce((acc, cur) => {
      const uploadingCur = cur?.filter(({ isFetching }) => isFetching);
      return uploadingCur?.length ? acc + uploadingCur?.length : acc;
    }, 0);
    !disConsole &&
      console.log(
        '%c 执行[getConcurrencyUploadingCount]',
        'font-size:14px; background:pink; color:#bf2c9f;',
        {
          count,
        },
        taskStack?.current,
      );
    return count;
  };

  /**
   * utils function
   * @func getWaitingTask
   * 取出等待上传队列的文件任务
   * @return number
   *
   */
  const getWaitingTask = () => {
    // 上传多文件时，只要并行线程池没满，就取出并发上传
    const waitingFileIndex = (Object.values(sliceQueue.current) as Array<any>).findIndex(queue => !!queue?.length);
    const waitingFileMd5 = Object.keys(sliceQueue.current)?.[`${waitingFileIndex}`];
    return sliceQueue.current?.[`${waitingFileMd5}`].pop();
  };

  /**
   * utils function
   * @func getWaitingUploadCount
   * 计算等待上传的文件块数
   * @return number
   *
   */
  const getWaitingUploadCount = () => {
    const count = (Object.values(sliceQueue.current) as Array<any>).reduce((acc, cur) => {
      return !!cur?.length ? acc + cur?.length : acc;
    }, 0);
    return count;
  };

  /**
   * utils function
   * @func computeFileProgress
   * 计算某个文件的上传进度
   * @param fileMd5 文件的唯一标识
   * @return number
   *
   */
  const computeFileProgress = (fileMd5, batchUuid) => {
    const completedFile = completedCountRef.current?.[batchUuid]?.[fileMd5];
    const uploadedSize =
      completedFile?.uploadStatus?.reduce?.((acc, { size, progress }) => acc + size * progress, 0) || 0;
    const progress =
      completedFile?.file?.size === 0 && completedFile?.uploadStatus?.[0]?.progress === 1
        ? 1
        : (uploadedSize / completedFile?.file?.size).toFixed(2) || 0;
    console.log(
      '%c 执行[computeFileProgress]',
      'font-size:14px; background:pink; color:#bf2c9f;',
      {
        batchUuid,
        progress,
        uploadedSize,
        completedFile,
        fileMd5,
      },
      completedFile?.uploadStatus,
    );
    return progress;
  };

  /**
   * utils function
   * @func  computeBatchProgress
   * 计算某批列表的上传进度
   * @param batchUuid 某上传批次的唯一标识
   * @return number
   *
   */
  const computeBatchProgress = batchUuid => {
    const completedFilesProgress = Object.values(completedCountRef.current?.[batchUuid])?.reduce(
      (acc, completedFile) => {
        const uploadedSize =
          completedFile?.uploadStatus?.reduce?.((acc, { size, progress }) => acc + size * progress, 0) || 0;
        return uploadedSize + acc;
      },
      0,
    );
    const total = totalSize.current?.[`${batchUuid}`];
    !disConsole &&
      console.log('%c 执行[computeBatchProgress]', 'font-size:14px; background:pink; color:#bf2c9f;', {
        batchUuid,
        completedFilesProgress,
        total,
      });
    return (completedFilesProgress / total).toFixed(2);
  };

  /**
   * export function
   * @func  batchUpload
   * 批量上传文件列表
   * @param files 待上传的文件列表
   * @param batchUuid 某上传批次的唯一标识
   * @param onSuccess 列表上传成功的回调函数
   * @param onError 列表上传失败的回调函数
   * @param onTotalProgress 上传进度的更新的回调函数
   * @param onFileSuc 文件上传成功的回调函数
   * @param onFileFail 文件上传失败的回调函数
   *
   * 步骤：
   * 1.initBatchUploadStatus 初始化【批上传】状态
   * 2.遍历文件列表 @func startFileUpload
   */
  const batchUpload = async ({
    files,
    batchUuid = uuidv4(),
    fileType,
    onSuccess,
    onError,
    onTotalProgress,
    onFileSuc,
    onFileFail,
  }: BatchUploadProps) => {
    /**
     * inner function
     * @func  onProgress
     * 计算并更新上传进度，触发进度更新回调
     * @param batchUuid
     * @returns null
     */
    const onProgress = (fileId, batchUuid) => {
      const progress = computeFileProgress(`${batchUuid}-${fileId}`, batchUuid);
      const totalProgress = computeBatchProgress(batchUuid);
      onTotalProgress?.(batchUuid, fileId, progress, totalProgress);
    };

    /**
     * inner function
     * @func  wrapOnSuccess
     * 单个文件上传成功后执行
     * @param batchUuid
     * @param downloadUrl
     * @param file
     * * 步骤：
     * 1.触发单文件成功回调@func onFileSuc
     * 2.更新状态值
     * 3. 检查当前上传状态=》继续执行or成功
     *@returns null
     */
    const wrapOnSuccess = (batchUuid, downloadUrl: string, file) => {
      const fileMd5 = `${batchUuid}-${file.uid}`;
      !disConsole &&
        console.log('%c 文件上传成功-[wrapOnSuccess]执行', 'font-size:14px; background:green; color:#bf2c9f;', {
          batchUuid,
          fileMd5,
        });
      Object.assign(status.current, {
        [`${batchUuid}`]: Object.assign(status.current?.[`${batchUuid}`], { [`${fileMd5}`]: UploadStatus.SUCCESS }),
      });

      onFileSuc?.(file, batchUuid, downloadUrl);

      cancelRequests(fileMd5);
      const isAllSuc = Object.values(status.current?.[`${batchUuid}`])?.every(
        status => status === UploadStatus.SUCCESS || status === UploadStatus.DELETED,
      );
      !disConsole &&
        console.log(
          '%c 执行检查当前status.current状态',
          'font-size:12px; background:yellow; color:#bf2c9f;',
          JSON.stringify(status.current?.[`${batchUuid}`]),
        );
      if (isAllSuc) {
        !disConsole && console.log('%c 全部上传成功', 'font-size:12px; background:yellow; color:#bf2c9f;');
        onSuccess?.(file, batchUuid, {
          totalSize: totalSize?.current?.[`${batchUuid}`],
          duration: Date.now() - (startTime?.current?.[`${batchUuid}`] as number),
        });
      }
      execNextTask(batchUuid);
    };
    /**
     * inner function
     * @func   wrapOnError
     * 上传失败（重试机会耗尽）回调@func onFileFail 、@func onError
     * @param batchUuid
     * @param file
     * @param error
     *@returns null
     */
    const wrapOnError = (batchUuid, file, error) => {
      const fileMd5 = `${batchUuid}-${file.uid}`;
      !disConsole &&
        console.log('%c 文件上传失败-[wrapOnError]执行', 'font-size:14px; background:red; color:#bf2c9f;', {
          batchUuid,
          fileMd5,
        });
      Object.assign(status.current, {
        [`${batchUuid}`]: Object.assign(status.current?.[`${batchUuid}`], { [`${fileMd5}`]: UploadStatus.FAIL }),
      });
      !disConsole &&
        console.log(
          '%c 执行检查当前status.current状态',
          'font-size:12px; background:yellow; color:#bf2c9f;',
          JSON.stringify(status.current?.[`${batchUuid}`]),
        );
      cancelRequests(fileMd5);
      !disConsole &&
        console.log(`
      status:${JSON.stringify(status.current?.[`${batchUuid}`])},
      completedCountRef:${JSON.stringify(completedCountRef.current?.[`${batchUuid}`])},
      sliceQueue:${JSON.stringify(sliceQueue.current)},
      taskStack:${JSON.stringify(taskStack.current)},
      retryMap:${JSON.stringify(retryMap.current)},
      `);
      openSentry && Sentry.captureException(new Error(`提交失败${batchUuid}:${JSON.stringify(error)}`));
      onFileFail?.(file, batchUuid);
      onError?.(file, batchUuid);
    };

    /**
     * inner function
     * @func  chunkUpload
     * 上传文件分片
     * @param batchUuid
     * @param sliceInfo
     * @param retry 失败后重试上传
     * @returns boolean 并行进程池是否还有空余
     * 步骤：
     * 1.计算waitingCount、uploadingCount
     * 2.不超过并行限制时，加载新的上传进程 @func getWaitingTask
     * 3. @func checkCompleteTask检查 分片的文件的上传情况
     * 4.计算、并返回——当前并行进程池是否还有空余
     */
    const chunkUpload = async (sliceInfo: Slice, retry = false) => {
      const { batchUuid } = sliceInfo;
      !disConsole &&
        console.log('%c [chunkUpload]执行', 'font-size:14px; background:red; color:#bf2c9f;', {
          batchUuid,
          sliceInfo,
        });
      const onSliceSuccess = (slice, res) => {
        const batchUuid = sliceInfo.batchUuid;
        const fileMd5 = `${sliceInfo.batchUuid}-${sliceInfo?.file?.uid}`;
        !disConsole &&
          console.log('%c [onSliceSuccess]执行', 'font-size:14px; background:red; color:#bf2c9f;', {
            batchUuid,
            slice,
            fileMd5,
          });
        // 上传请求成功后,  将当前分片出栈
        Object.assign(taskStack.current, {
          [`${fileMd5}`]: taskStack.current?.[`${fileMd5}`]?.filter(
            (item: UploadTask) => item?.slice?.seq !== slice.seq,
          ),
        });
        res &&
          Object.assign(shuyuanSliceTags.current, {
            [`${fileMd5}`]: [...shuyuanSliceTags.current?.[`${fileMd5}`], res],
          });
        completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`]?.uploadStatus?.forEach(status => {
          if (status.seq === slice.seq) {
            status.progress = 1;
          }
        });
        onProgress(slice.file.uid, slice.batchUuid);
        // 检查是否还有分片待上传，或可以结束上传任务
        execNextTask(batchUuid);
      };

      const onSliceProgress = (slice: Slice, percent: number) => {
        const { batchUuid } = slice;
        const fileMd5 = `${batchUuid}-${slice?.file?.uid}`;
        !disConsole &&
          console.log(
            '%c [onSliceProgress]执行',
            'font-size:14px; background:green; color:#bf2c9f;',
            {
              batchUuid,
              percent,
              fileMd5,
            },
            slice.seq,
            completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`],
          );
        try {
          completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`].uploadStatus?.forEach(status => {
            if (status.seq === slice.seq) {
              status.progress = Math.max(percent, status.progress);
            }
          });
        } catch (e) {
          openSentry &&
            Sentry.captureException(new Error(`${batchUuid}${fileMd5}:${JSON.stringify(completedCountRef.current)}`));
        }
        onProgress(slice.file.uid, slice.batchUuid);
      };

      const sendRequest = (slice: Slice) => {
        const { start, end, seq, file, batchUuid } = slice;
        const fileMd5 = `${batchUuid}-${sliceInfo?.file?.uid}`;
        const shuyuanId = completedCountRef.current?.[batchUuid]?.[fileMd5]?.shuyuanId;
        try {
          services.taskVersionServer.chunkUpload(
            setUploadTaskStack(slice, retry),
            file as any,
            file.slice(start, end),
            batchUuid,
            seq + 1,
            fileType,
            shuyuanId,
            shuyuanSliceId => onSliceSuccess(slice, shuyuanSliceId),
            error => onUploadTaskError(slice, error),
            param => onSliceProgress(slice, formatRequestProgress(param)),
          );
        } catch (e) {
          onUploadTaskError(slice, e);
        }
      };
      const fileMd5 = `${batchUuid}-${sliceInfo?.file?.uid}`;
      if (
        [UploadStatus.FAIL, UploadStatus.DELETED, UploadStatus.SUCCESS]?.includes(
          status.current?.[`${batchUuid}`]?.[`${fileMd5}`],
        )
      ) {
        //已结束的文件不再上传
        message.info('文件已结束,不再上传');
        return;
      }

      sendRequest(sliceInfo);
    };

    /**
     * inner function
     * @func  checkCompleteTask
     * @param batchUuid
     * 检查并行下载进程，并加载新的上传进程
     *
     * 步骤：
     * 1.根据completedCountRef计算已上传的文件size
     * 2.针对已上传完成的分片文件发起chunkSuccess组装请求
     */
    const checkCompleteTask = async batchUuid => {
      !disConsole &&
        console.log('%c [checkCompleteTask]执行', 'font-size:14px; background:red; color:#bf2c9f;', {
          batchUuid,
        });
      const completeFiles = (Object.values(completedCountRef?.current?.[`${batchUuid}`]) as Array<any>).filter(
        completeTask => {
          const isChunkAllSuccess = completeTask?.uploadStatus?.every(({ progress }) => progress === 1);
          return completeTask && completeTask?.fileMd5 && batchUuid && isChunkAllSuccess;
        },
      );
      completeFiles.forEach(({ fileMd5, name, fileId, file }) => {
        const isEnd = [UploadStatus.SUCCESS, UploadStatus.FAIL].includes(
          status.current?.[`${batchUuid}`]?.[`${fileMd5}`],
        );
        const isCanceled = status.current?.[`${batchUuid}`]?.[`${fileMd5}`] === UploadStatus.DELETED;
        const isAssemble = status.current?.[`${batchUuid}`]?.[`${fileMd5}`] === UploadStatus.ASSEMBLE;

        if (isEnd || isCanceled || isAssemble) {
          return;
        }
        const shuyuanId = completedCountRef.current?.[batchUuid]?.[fileMd5]?.shuyuanId;
        const shuyuanSlices = shuyuanSliceTags.current?.[fileMd5];
        if (file?.size > CHUNK_SIZE) {
          Object.assign(status.current, {
            [`${batchUuid}`]: Object.assign(status.current?.[`${batchUuid}`], {
              [`${fileMd5}`]: UploadStatus.ASSEMBLE,
            }),
          });
          services.taskVersionServer
            .chunkSuccess(file, batchUuid, shuyuanId, file?.size, fileType, shuyuanSlices)
            .then(res => {
              const isSuc = (res as any)?.code === 10000;
              if (isSuc) {
                wrapOnSuccess(
                  batchUuid,
                  res?.data?.download_url,
                  completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`].file,
                );
              } else {
                wrapOnError(batchUuid, completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`].file, res);
              }
            })
            .catch(e => {
              wrapOnError(batchUuid, completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`].file, e);
            });
        }
      });
    };

    /**
     * inner function
     * @func  execNextTask
     * @param batchUuid
     * 检查并行下载进程，并加载新的上传进程
     * @returns boolean 并行进程池是否还有空余
     * 步骤：
     * 1.计算waitingCount、uploadingCount
     * 2.不超过并行限制时，加载新的上传进程 @func getWaitingTask
     * 3. @func checkCompleteTask检查 分片的文件的上传情况
     * 4.计算、并返回——当前并行进程池是否还有空余
     */
    const execNextTask = batchUuid => {
      const waitingCount = getWaitingUploadCount();
      const uploadingCount = getConcurrencyUploadingCount();
      !disConsole &&
        console.log('%c [execNextTask]执行', 'font-size:14px; background:red; color:#bf2c9f;', {
          batchUuid,
          waitingCount,
          uploadingCount,
        });
      // 限制并行请求数
      if (waitingCount > 0 && uploadingCount < CONCURRENCY_MAX) {
        const task = getWaitingTask();
        if (!!task) {
          !task?.isFileChunk ? normalUpload(task) : chunkUpload(task);
        }
      }
      checkCompleteTask(batchUuid);
      // 是否可以继续执行
      return getWaitingUploadCount() > 0 && getConcurrencyUploadingCount() < CONCURRENCY_MAX;
    };

    /**
     * inner function
     * @func  setUploadTaskStack
     * 为上传请求注册controller.signal，并更新在taskStack
     * @param sliceInfo
     * @returns controller.signal控制请求的取消
     * 步骤：
     * 1.new AbortController()
     * 2.更新taskStack的状态
     * 3.返回controller.signal
     */
    const setUploadTaskStack = (sliceInfo, retry) => {
      !disConsole &&
        console.log('%c [setUploadTaskStack]执行', 'font-size:14px; background:red; color:#bf2c9f;', sliceInfo, {
          retry,
        });
      const fileMd5 = `${sliceInfo.batchUuid}-${sliceInfo?.file?.uid}`;
      const controller = new AbortController(); // 浏览器fetch支持AbortController https://developer.mozilla.org/zh-CN/docs/Web/API/AbortController
      if (retry) {
        Object.assign(taskStack.current, {
          [`${fileMd5}`]: taskStack.current?.[`${fileMd5}`]?.map((item: UploadTask) => {
            let task = item;
            if (task?.slice?.seq === sliceInfo.seq) {
              task.controller = controller;
              task.isFetching = true;
            }
            return task;
          }),
        });
      } else {
        Object.assign(taskStack.current, {
          [`${fileMd5}`]: (taskStack.current?.[`${fileMd5}`] || [])?.concat({
            slice: sliceInfo,
            controller: controller,
            isFetching: true,
          }),
        });
      }
      return controller.signal;
    };

    /**
     * inner function
     * @func  onUploadTaskError
     * 上传请求失败回调
     * @param sliceInfo
     * @param error
     *@returns null
     */
    const onUploadTaskError = (sliceInfo: Slice, error) => {
      const batchUuid = sliceInfo.batchUuid;
      const fileMd5 = `${batchUuid}-${sliceInfo?.file?.uid}`;
      !disConsole &&
        console.log('%c [onUploadTaskError]执行', 'font-size:14px; background:red; color:#bf2c9f;', {
          batchUuid,
          sliceInfo,
          error,
        });
      if (error === 'ABANDONED_BY_USER') {
        return;
      }
      // 重试分片上传
      let retry = retryMap.current?.[`${fileMd5}`];
      if (retry) {
        Object.assign(retry, { [sliceInfo.seq]: retry?.[sliceInfo.seq] || 0 });
      } else {
        Object.assign(retryMap.current, { [`${fileMd5}`]: { [sliceInfo.seq]: 0 } });
        retry = retryMap.current?.[`${fileMd5}`];
      }
      if (retry[sliceInfo.seq] < RETRY_MAX) {
        const _task = taskStack?.current?.[`${fileMd5}`]?.find(
          (item: UploadTask) => item?.slice?.seq === sliceInfo.seq,
        );

        _task && Object.assign(_task, { isFetching: false });
        retry[sliceInfo.seq] = retry[sliceInfo.seq] + 1;
        try {
          completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`].uploadStatus?.forEach(status => {
            if (status.seq === sliceInfo.seq) {
              status.progress = 0;
            }
          });
        } catch (e) {
          openSentry &&
            Sentry.captureException(new Error(`${batchUuid}${fileMd5}:${JSON.stringify(completedCountRef.current)}`));
        }
        !sliceInfo?.isFileChunk ? normalUpload(sliceInfo) : chunkUpload(sliceInfo, true);
        return;
      }
      wrapOnError(batchUuid, sliceInfo?.file, error);
    };

    const normalUpload = (sliceInfo: Slice) => {
      const { batchUuid } = sliceInfo;
      const fileMd5 = `${batchUuid}-${sliceInfo?.file?.uid}`;
      !disConsole &&
        console.log('%c 不分片执行[normalUpload]上传', 'font-size:12px; background:pink; color:#bf2c9f;', {
          sliceInfo,
        });
      const file = sliceInfo.file;
      services.taskVersionServer
        .normalUpload(setUploadTaskStack(sliceInfo, false), file as any, batchUuid, fileType, param => {
          const percent = formatRequestProgress(param);
          completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`]?.uploadStatus?.forEach?.(status => {
            if (status.seq === sliceInfo.seq) {
              status.progress = percent;
            }
          });
          onProgress(sliceInfo.file.uid, sliceInfo.batchUuid);
        })
        .then(res => {
          const { code, data, message } = res as any;
          const isSuc = code === 10000;
          console.log('normalUpload response:', JSON.stringify(res));
          if (isSuc) {
            // 上传请求成功后,  将当前分片出栈
            Object.assign(taskStack.current, {
              [`${fileMd5}`]: taskStack.current?.[`${fileMd5}`]?.filter(
                (item: UploadTask) => item?.slice?.seq !== sliceInfo.seq,
              ),
            });
            // 更新上传进度
            try {
              completedCountRef.current?.[`${batchUuid}`]?.[`${fileMd5}`].uploadStatus?.forEach(status => {
                if (status.seq === sliceInfo.seq) {
                  console.log({ status });
                  status.progress = 1;
                }
              });
            } catch (e) {
              openSentry &&
                Sentry.captureException(
                  new Error(`${batchUuid}${fileMd5}:${JSON.stringify(completedCountRef.current)}`),
                );
            }
            wrapOnSuccess(batchUuid, data?.download_url, file);
          } else {
            onUploadTaskError(sliceInfo, new Error(message));
          }
        })
        .catch(e => {
          console.log('normalUpload error:', JSON.stringify(e));
          onUploadTaskError(sliceInfo, e);
        });
      return;
    };

    /**
     * inner function
     * @func  startFileUpload
     * 批量上传文件列表
     * @param file 待上传的文件
     *
     * 步骤：
     * 1.设置相应文件状态为 UploadStatus.UPLOADING
     * 2.计算slicesCount、并切片文件，分别处理普通文件和分片文件，放入对应上传状态ref
     * 3.execNextTask持续执行上传
     */
    const startFileUpload = async (batchUuid, file) => {
      const fileMd5 = `${batchUuid}-${file?.uid}`;
      !disConsole &&
        console.log('%c 执行startFileUpload', 'font-size:12px; background:pink; color:#bf2c9f;', {
          file,
          fileMd5,
        });
      Object.assign(status.current, {
        [`${batchUuid}`]: Object.assign(status.current?.[`${batchUuid}`], {
          [`${fileMd5}`]: UploadStatus.UPLOADING,
        }),
      });
      const slicesCount = Math.ceil(file.size / CHUNK_SIZE);
      let i = 0;
      const totalSlices: SliceUploadingStatus[] = [];
      console.log({ slicesCount });
      while (i < slicesCount) {
        // 分片上传线程是分片级别，分片需要标记文件
        const seq = i;
        const start = i * CHUNK_SIZE;
        const end = Math.min((i + 1) * CHUNK_SIZE, file.size);
        totalSlices.unshift({
          file,
          seq,
          start,
          end,
          size: end - start,
          progress: 0,
          isFileChunk: slicesCount > 1,
          batchUuid,
        });
        i = i + 1;
      }
      if (slicesCount === 0) {
        totalSlices.unshift({
          file,
          seq: 0,
          start: 0,
          end: 0,
          size: 0,
          progress: 0,
          isFileChunk: false,
          batchUuid,
        });
      }
      console.log({ slicesCount }, totalSlices?.length);
      if (slicesCount <= 1) {
        initialFileNormalUpload(batchUuid, fileMd5, totalSlices, file);
      } else {
        await initialFileChunkUpload(batchUuid, fileMd5, totalSlices, file);
      }
      let ok = true;
      while (ok) {
        ok = execNextTask(batchUuid);
      }
    };

    !disConsole &&
      console.log('%c [batchUpload]执行', 'font-size:14px; background:pink; color:#bf2c9f;', { batchUuid, files });
    initBatchUploadStatus(
      batchUuid,
      files?.reduce((acc, file) => acc + file?.size, 0),
    );
    files.forEach(file => {
      startFileUpload(batchUuid, file);
    });
  };
  /**
   * export function
   * @func cancelFileUpload
   * 取消某个文件上传
   * @param batchUuid 当前上传批次的唯一标识
   * @param fileMd5 当前上传文件的唯一标识
   * @return null
   *
   * 步骤
   * 1.取消进行中的网络请求
   * 2.更新该文件相关的状态记录
   */
  const cancelFileUpload = (fileMd5, batchUuid) => {
    !disConsole &&
      console.log('%c 执行[cancelFileUpload]', 'font-size:14px; background:pink; color:#bf2c9f;', {
        batchUuid,
        fileMd5,
      });
    if (!status.current?.[`${batchUuid}`]) {
      //尚未开始上传
      return;
    }
    // 删除文件状态，防止影响全局状态判断
    Object.assign(status.current?.[`${batchUuid}`] || {}, { [`${fileMd5}`]: UploadStatus.DELETED });
    Object.assign(completedCountRef.current?.[`${batchUuid}`], { [`${fileMd5}`]: undefined });
    const filesSize = totalSize.current?.[`${batchUuid}`];
    // todo减去fileSize
    // Object.assign(totalSize.current, { [`${batchUuid}`]: filesSize });
    if (!status.current?.[`${batchUuid}`]) {
      return;
    }
    Object.assign(sliceQueue.current?.[`${fileMd5}`] || {}, []);
    cancelRequests(`${batchUuid}-${fileMd5}`);
    const isAllSuc = Object.values(status.current?.[`${batchUuid}`])?.every(
      status => status === UploadStatus.SUCCESS || status === UploadStatus.DELETED,
    );
    console.log('执行[cancelFileUpload]-检查批次状态:status.current', JSON.stringify(status.current?.[`${batchUuid}`]));
    if (isAllSuc) {
      message.success('上传成功');
    }
  };

  /**
   * export function
   * @func cancelBatchUpload
   * 取消某次批量上传
   * @param batchUuid 当前上传批次的唯一标识
   * @return null
   *
   * 步骤
   * 1.取消进行中的网络请求
   * 2.更新相关的状态记录
   */
  const cancelBatchUpload = batchUuid => {
    !disConsole &&
      console.log('%c 执行[cancelBatchUpload]', 'font-size:12px; background:pink; color:#bf2c9f;', {
        batchUuid,
      });
    if (!status.current?.[`${batchUuid}`]) {
      return;
    }
    const frozenStatus = {};
    Object.keys(status.current?.[`${batchUuid}`]).forEach(fileMd5 => {
      Object.assign(frozenStatus, { [`${fileMd5}`]: UploadStatus.DELETED });
      taskStack.current?.[`${fileMd5}`]?.forEach((item: UploadTask) => {
        item.controller.abort('ABANDONED_BY_USER');
        item.isFetching = false;
      });
      Object.assign(shuyuanSliceTags.current, { [`${fileMd5}`]: [] }); // 分片集合
      Object.assign(retryMap.current, { [`${fileMd5}`]: {} });
      Object.assign(sliceQueue.current, { [`${fileMd5}`]: undefined }); // 分片集合
      Object.assign(taskStack.current, { [`${fileMd5}`]: [] }); // 网络并行请求进程集合
      Object.assign(retryMap.current, { [`${fileMd5}`]: {} });
    });
    Object.assign(status.current, {
      [`${batchUuid}`]: frozenStatus,
    });
    Object.assign(startTime.current, { [`${batchUuid}`]: undefined });
    Object.assign(totalSize.current, { [`${batchUuid}`]: undefined });
    Object.assign(status.current, { [`${batchUuid}`]: undefined });
    Object.assign(completedCountRef.current, { [`${batchUuid}`]: undefined });
  };

  return { batchUpload, cancelFileUpload, cancelBatchUpload };
};
